(() => {
  window.AW = window.AW || {};

  if (window.AW.ServiceCardsHeightSync) {
    return;
  }

  window.AW.ServiceCardsHeightSync = true;

  const instances = new Map();
  const tabletQuery = "(min-width: 768px) and (max-width: 1099px)";
  const rowTolerance = 2;
  const heightTolerance = 0.5;

  class AWServiceCardsHeightSync {
    constructor(root) {
      this.root = root;
      this.syncFrame = null;
      this.onResize = this.onResize.bind(this);

      window.addEventListener("resize", this.onResize, { passive: true });
      this.scheduleSync();
    }

    getCards() {
      const grid = this.root.querySelector(".aw-service-cards__grid");

      if (!(grid instanceof HTMLElement)) {
        return [];
      }

      return Array.from(grid.children).filter((card) =>
        card instanceof HTMLElement
        && card.classList.contains("aw-service-cards__card"),
      );
    }

    clearCorrections(cards) {
      cards.forEach((card) => {
        card.querySelector(".aw-service-cards__body")?.style.removeProperty(
          "--aw-service-cards-badge-reserve",
        );
        card.querySelector(".aw-service-cards__heading")?.style.removeProperty(
          "--aw-service-cards-heading-min-height",
        );
      });
    }

    getCardRows(cards) {
      const rows = [];

      cards.forEach((card) => {
        const top = card.getBoundingClientRect().top;
        const row = rows.find((candidate) =>
          Math.abs(candidate.top - top) <= rowTolerance,
        );

        if (row) {
          row.cards.push(card);
          return;
        }

        rows.push({ top, cards: [card] });
      });

      return rows
        .filter(({ cards: rowCards }) => rowCards.length >= 2)
        .map(({ cards: rowCards }) => rowCards);
    }

    isTabletThreeColumn() {
      return window.matchMedia(tabletQuery).matches
        && this.root.classList.contains("aw-service-cards--tb-columns-3");
    }

    sync() {
      const cards = this.getCards();

      this.clearCorrections(cards);

      if (!this.isTabletThreeColumn()) {
        return;
      }

      this.getCardRows(cards).forEach((rowCards) => {
        const measurements = rowCards.map((card) => {
          const body = card.querySelector(".aw-service-cards__body");
          const heading = card.querySelector(".aw-service-cards__heading");
          const bodyRect = body?.getBoundingClientRect();
          const headingRect = heading?.getBoundingClientRect();

          return {
            body,
            heading,
            hasBadge: Boolean(
              card.querySelector(".aw-service-cards__badge-slot"),
            ),
            badgeOffset: bodyRect && headingRect
              ? Math.max(0, headingRect.top - bodyRect.top)
              : 0,
            headingHeight: headingRect?.height || 0,
          };
        });

        const maxBadgeOffset = Math.max(
          ...measurements.map(({ badgeOffset }) => badgeOffset),
        );

        measurements.forEach(({ body, hasBadge, badgeOffset }) => {
          const reserve = maxBadgeOffset - badgeOffset;

          if (body && !hasBadge && reserve > heightTolerance) {
            body.style.setProperty(
              "--aw-service-cards-badge-reserve",
              `${reserve}px`,
            );
          }
        });

        const maxHeadingHeight = Math.max(
          ...measurements.map(({ headingHeight }) => headingHeight),
        );

        measurements.forEach(({ heading, headingHeight }) => {
          const heightDifference = maxHeadingHeight - headingHeight;

          if (heading && heightDifference > heightTolerance) {
            heading.style.setProperty(
              "--aw-service-cards-heading-min-height",
              `${maxHeadingHeight}px`,
            );
          }
        });
      });
    }

    scheduleSync() {
      if (this.syncFrame !== null) {
        window.cancelAnimationFrame(this.syncFrame);
      }

      this.syncFrame = window.requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    onResize() {
      this.scheduleSync();
    }

    destroy() {
      if (this.syncFrame !== null) {
        window.cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
      }

      window.removeEventListener("resize", this.onResize);
      this.clearCorrections(this.getCards());
    }
  }

  const getRoot = (target) => {
    if (!(target instanceof Element)) {
      return null;
    }

    return target.matches("[data-aw-service-cards]")
      ? target
      : target.querySelector("[data-aw-service-cards]");
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) {
      return;
    }

    instances.set(root, new AWServiceCardsHeightSync(root));
  };

  const destroyRoot = (root) => {
    const instance = instances.get(root);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(root);
  };

  const initAll = () => {
    document.querySelectorAll("[data-aw-service-cards]").forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = getRoot(event.target);

    if (root) {
      initRoot(root);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (root) {
      destroyRoot(root);
    }
  });
})();
